import { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from './AuthContext';
import type { AuthAccount } from './useAuth';
import type { ImpersonatedUserInfo } from '../features/admin/services/adminApi';

export const IMPERSONATE_KEY = 'beacon_impersonate';

interface ImpersonateContextValue {
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUserInfo | null;
  effectiveAccount: AuthAccount | null;
  startImpersonation: (user: ImpersonatedUserInfo) => void;
  stopImpersonation: () => void;
}

const ImpersonateContext = createContext<ImpersonateContextValue | null>(null);

function readStoredImpersonation(): ImpersonatedUserInfo | null {
  try {
    const stored = sessionStorage.getItem(IMPERSONATE_KEY);
    return stored ? (JSON.parse(stored) as ImpersonatedUserInfo) : null;
  } catch {
    return null;
  }
}

export function ImpersonateProvider({ children }: { children: React.ReactNode }) {
  const { account } = useAuthContext();
  const navigate = useNavigate();
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUserInfo | null>(
    readStoredImpersonation
  );

  const startImpersonation = (user: ImpersonatedUserInfo) => {
    sessionStorage.setItem(IMPERSONATE_KEY, JSON.stringify(user));
    setImpersonatedUser(user);
  };

  const stopImpersonation = () => {
    sessionStorage.removeItem(IMPERSONATE_KEY);
    setImpersonatedUser(null);
    navigate('/');
  };

  const effectiveAccount: AuthAccount | null = impersonatedUser
    ? {
        oid: impersonatedUser.oid,
        email: impersonatedUser.email,
        name: impersonatedUser.displayName,
        tenantId: account?.tenantId ?? 'unknown',
        systemRole: impersonatedUser.systemRole,
        allowedUserId: impersonatedUser.allowedUserId,
      }
    : account;

  return (
    <ImpersonateContext.Provider
      value={{
        isImpersonating: impersonatedUser !== null,
        impersonatedUser,
        effectiveAccount,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </ImpersonateContext.Provider>
  );
}

export function useImpersonate(): ImpersonateContextValue {
  const ctx = useContext(ImpersonateContext);
  if (!ctx) throw new Error('useImpersonate must be used within ImpersonateProvider');
  return ctx;
}
